import { isVisibleOnProviderDashboard } from './providerBookingFlow';

const ACTIVE_PHARMACY_STATUSES = new Set([
  'payment_pending', 'paid', 'searching_pharmacy', 'pharmacy_accepted',
  'pharmacy_confirmed', 'preparing', 'ready_for_pickup', 'ranger_searching',
  'ranger_assigned', 'ranger_arriving_pickup', 'ready_for_handover', 'picked_up',
  'ranger_arriving_customer', 'out_for_delivery',
]);

const ACTIVE_LAB_STATUSES = new Set([
  'payment_pending', 'confirmed', 'collector_en_route', 'collector_arrived',
  'sample_collected', 'processing',
]);

const ACTIVE_DELIVERY_STATUSES = new Set([
  'ranger_assigned', 'ranger_arriving_pickup', 'ready_for_handover',
  'picked_up', 'ranger_arriving_customer', 'out_for_delivery',
]);

const readableStatus = (status) => String(status || 'in progress').replace(/_/g, ' ');

function marketplaceCard(order, type) {
  const id = order?.id;
  if (!id) return null;

  if (type === 'pharmacy' && !ACTIVE_PHARMACY_STATUSES.has(order.status)) return null;
  if (type === 'lab' && !ACTIVE_LAB_STATUSES.has(order.status)) return null;
  if (type === 'delivery' && !ACTIVE_DELIVERY_STATUSES.has(order.status)) return null;

  const title = type === 'pharmacy'
    ? (order.order_type === 'prescription' ? 'Prescription order' : 'Pharmacy order')
    : type === 'lab' ? 'Lab booking' : 'Pharmacy delivery';

  return {
    id,
    key: `${type}:${id}`,
    type,
    title,
    status: readableStatus(order.status),
    route: type === 'lab' ? '/provider/labs' : '/provider/pharmacy',
    createdAt: order.created_at || '',
  };
}

/**
 * Builds the provider's dashboard cards from the distinct order systems.
 * General service bookings, pharmacy orders, lab bookings and Ranger delivery
 * orders live in separate backend collections and must not be treated as one API.
 */
export function buildProviderActiveOrders({
  activeBooking,
  provider,
  pharmacyOrders = [],
  labOrders = [],
  deliveryOrders = [],
}) {
  const cards = [];

  if (isVisibleOnProviderDashboard(activeBooking)) {
    cards.push({
      id: activeBooking.id,
      key: `booking:${activeBooking.id}`,
      type: 'booking',
      title: activeBooking.service_label || activeBooking.service_key || 'Service booking',
      status: readableStatus(activeBooking.travel_status || activeBooking.status),
      route: `/provider/bookings/${activeBooking.id}`,
      createdAt: activeBooking.created_at || '',
    });
  }

  if (provider?.category === 'pharmacy') {
    pharmacyOrders.forEach((order) => {
      const card = marketplaceCard(order, 'pharmacy');
      if (card) cards.push(card);
    });
  }

  if (provider?.category === 'lab_test') {
    labOrders.forEach((order) => {
      const card = marketplaceCard(order, 'lab');
      if (card) cards.push(card);
    });
  }

  if (provider?.delivery_enabled) {
    deliveryOrders.forEach((order) => {
      const card = marketplaceCard(order, 'delivery');
      if (card) cards.push(card);
    });
  }

  // When the pharmacy and Ranger modes belong to the same account, show a
  // single card for an order that is currently being delivered, not duplicates.
  const deduped = new Map();
  cards.forEach((card) => {
    const previous = deduped.get(card.id);
    if (!previous || card.type === 'delivery') deduped.set(card.id, card);
  });

  return [...deduped.values()].sort((a, b) => {
    if (a.type === 'booking' && b.type !== 'booking') return -1;
    if (b.type === 'booking' && a.type !== 'booking') return 1;
    return String(b.createdAt).localeCompare(String(a.createdAt));
  });
}
